import { forwardRef, useState } from "react";
import { Input, type InputProps } from "../Input/Input";

export interface PasswordInputProps extends Omit<InputProps, "type" | "trailing"> {
  /** Etiqueta accesible del ojo cuando la contraseña está oculta. */
  showLabel?: string;
  /** Etiqueta accesible del ojo cuando la contraseña está visible. */
  hideLabel?: string;
}

/**
 * Contraseña con ojo de mostrar/ocultar.
 *
 * El ojo lleva `tabIndex={-1}` a propósito: quien navega con teclado
 * escribe la contraseña y pasa al siguiente campo; un botón decorativo
 * en medio del recorrido estorba. Sigue siendo alcanzable con el ratón y
 * anunciado por lector de pantalla con un `aria-label` que cambia según
 * el estado.
 */
export const PasswordInput = forwardRef<HTMLInputElement, PasswordInputProps>(
  function PasswordInput(
    { showLabel = "Mostrar contraseña", hideLabel = "Ocultar contraseña", ...rest },
    ref,
  ) {
    const [visible, setVisible] = useState(false);

    return (
      <Input
        ref={ref}
        type={visible ? "text" : "password"}
        trailing={
          <button
            type="button"
            tabIndex={-1}
            aria-label={visible ? hideLabel : showLabel}
            aria-pressed={visible}
            onClick={() => setVisible((value) => !value)}
            className="inline-flex h-9 w-9 items-center justify-center rounded-xl text-ds-ink-3 transition hover:bg-ds-mist/70 hover:text-ds-ink-3 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ds-violet-pop/50"
          >
            {visible ? <EyeOffIcon /> : <EyeIcon />}
          </button>
        }
        {...rest}
      />
    );
  },
);

/* Los dos SVG del sistema actual, tal cual. */

function EyeIcon() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      className="h-5 w-5"
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      aria-hidden="true"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={1.5}
        d="M2.036 12.322C3.423 7.67 7.36 5 12 5c4.64 0 8.577 2.67 9.964 7.322a.8.8 0 010 .356C20.577 17.33 16.64 20 12 20c-4.64 0-8.577-2.67-9.964-7.322a.8.8 0 010-.356z"
      />
      <circle cx="12" cy="12" r="3" strokeWidth={1.5} />
    </svg>
  );
}

function EyeOffIcon() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      className="h-5 w-5"
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      aria-hidden="true"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={1.5}
        d="M3 3l18 18M10.58 10.59A2.001 2.001 0 1113.41 13.4M9.88 5.13A9.71 9.71 0 0112 5c6 0 9 7 9 7a15.76 15.76 0 01-3.28 4.52M6.1 6.05A15.77 15.77 0 003 12s3 7 9 7c1.28 0 2.5-.27 3.62-.75"
      />
    </svg>
  );
}
